import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from "@egaranti/components";
import { Textarea } from "@egaranti/components";
import { zodResolver } from "@hookform/resolvers/zod";

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import ProductService from "@/services/productService";

import ProductSelector from "@/components/products/productSelector";
import { Breadcrumb, BreadcrumbItem } from "@/components/shared/breadcrumbs";

import * as z from "zod";

const schemaIndividual = (t) =>
  z.object({
    merchantProductIds: z.array(z.string()),
    firstName: z.string().min(1, t("common.nonEmpty")),
    lastName: z.string().min(1, t("common.nonEmpty")),
    email: z.string().email({ message: t("common.validEmail") }),
    countryCode: z.string().min(1, t("common.nonEmpty")),
    phone: z.string().min(1, t("common.nonEmpty")),
    additionalText: z.string(),
    billingFile: z.any().optional(),
  });

const schemaCorporate = (t) =>
  z.object({
    merchantProductIds: z.array(z.string()),
    name: z.string().min(1, t("common.nonEmpty")),
    vkn: z.string().min(1, t("common.nonEmpty")),
    email: z.string().email({ message: t("common.validEmail") }),
    additionalText: z.string(),
    billingFile: z.any().optional(),
  });

const SendWarrantyPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [type, setType] = useState("individual");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const { t } = useTranslation();
  const { toast } = useToast();

  const form = useForm({
    resolver: zodResolver(
      type === "individual" ? schemaIndividual(t) : schemaCorporate(t),
    ),
    defaultValues:
      type === "individual"
        ? {
            firstName: "",
            lastName: "",
            email: "",
            countryCode: "TR",
            phone: "",
            merchantProductIds: [],
            additionalText: "",
          }
        : {
            name: "",
            vkn: "",
            email: "",
            merchantProductIds: [],
            additionalText: "",
          },
    mode: "onChange",
  });

  useEffect(() => {
    const defaultValues =
      type === "individual"
        ? {
            firstName: "",
            lastName: "",
            email: "",
            countryCode: "TR",
            phone: "",
            merchantProductIds: [],
            additionalText: "",
          }
        : {
            name: "",
            vkn: "",
            email: "",
            merchantProductIds: [],
            additionalText: "",
          };

    form.reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const productIds = selectedProducts.map((product) => product.id);
      const payload = {
        ...data,
        merchantProductIds: productIds,
      };

      if (type === "individual") {
        await ProductService.sendWarrantyIndividual(payload);
      } else {
        await ProductService.sendWarrantyCorporate(payload);
      }

      form.reset();
      setSelectedProducts([]);

      toast({
        title: t("productsPage.warrantySuccess"),
        variant: "success",
      });
    } catch (err) {
      toast({
        title: err.response?.data?.errors?.[0]?.message || t("common.error"),
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="flex min-h-screen flex-col bg-[#F9FAFC]">
      <Breadcrumb>
        <BreadcrumbItem link="/products/products">
          {t("productsPage.products")}
        </BreadcrumbItem>
        <BreadcrumbItem active>{t("productsPage.sendWarranty")}</BreadcrumbItem>
      </Breadcrumb>

      <div className="container mx-auto my-20 flex flex-col items-center justify-center gap-6">
        <div className="formBox">
          <h2 className="mb-5 text-lg font-medium">
            {t("productsPage.sendWarranty")}
          </h2>

          {/* Warranty Form */}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
              {/* Product Selector */}
              <div className="mb-6">
                <ProductSelector
                  selectedProducts={selectedProducts}
                  onSelect={setSelectedProducts}
                />
              </div>

              {/* Select Individual or Corporate */}
              <RadioGroup
                className="flex space-x-4 py-5"
                onValueChange={(value) => setType(value)}
                defaultValue={type}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="individual" id="individual" />
                  <Label htmlFor="individual">{t("common.individual")}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="corporate" id="corporate" />
                  <Label htmlFor="corporate">{t("common.corporate")}</Label>
                </div>
              </RadioGroup>
              {/* Conditional fields based on 'type' */}
              {type === "individual" ? (
                <>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("common.firstName")}</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("common.lastName")}</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="grid grid-cols-4 gap-4">
                      <FormField
                        control={form.control}
                        name="countryCode"
                        className="col-span-1"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("common.country")}</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue
                                    placeholder={t("common.country")}
                                  />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="TR">
                                  Türkiye (+90)
                                </SelectItem>
                                <SelectItem value="US">USA (+1)</SelectItem>
                                <SelectItem value="UZ">
                                  Uzbekistan (+998)
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem className="col-span-3">
                            <FormLabel>{t("common.phone")}</FormLabel>
                            <FormControl>
                              <Input type="tel" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("common.email")}</FormLabel>
                          <FormControl>
                            <Input type="email" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="billingFile"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t("payment.pastPayments.table.invoice")}
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="file"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                field.onChange(file);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="additionalText"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("claimsPage.address")}</FormLabel>
                          <FormControl>
                            <Textarea {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </>
              ) : (
                <div className="grid grid-cols-1 gap-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("common.companyName")}</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="vkn"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("common.taxIdentificationNumber")}</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("common.email")}</FormLabel>
                        <FormControl>
                          <Input type="email" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="billingFile"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("payment.pastPayments.table.invoice")}</FormLabel>
                        <FormControl>
                          <Input
                            type="file"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              field.onChange(file);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="additionalText"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("claimsPage.address")}</FormLabel>
                        <FormControl>
                          <Textarea {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}
            </form>
          </Form>
          <div className="flex w-full justify-end py-5">
            <Button
              size="lg"
              disabled={isLoading || selectedProducts.length === 0}
              onClick={form.handleSubmit(onSubmit)}
            >
              {t("common.save")}
            </Button>
          </div>
        </div>
      </div>
    </main>
  );
};

export default SendWarrantyPage;
